import React, { useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import { createCustomer } from '../api/customerApi';
import { getUsers } from '../api/authApi';
import styles from './AddCustomer.module.css';
import AppLayout from '../components/AppLayout';

const translations = {
  en: {
    addCustomer: 'Add New Customer',
    createCustomer: 'Create Customer',
    formIntro: 'Capture organization, location, and contact details to onboard the customer.',
    companySection: 'Organization details',
    assignmentSection: 'Assignment',
    locationSection: 'Location',
    contactSection: 'Primary contact',
    generatedCustomerId: 'Customer ID will be generated automatically.',
    customerName: 'Full Name:',
    enterCustomerName: 'Enter full name',
    organizationName: 'Organization Name:',
    enterOrganizationName: 'Enter organization name',
    businessActivity: 'Business Activity:',
    enterBusinessActivity: 'Enter business activity',
    governorate: 'Governorate:',
    enterGovernorate: 'Enter governorate',
    mainArea: 'Main Area:',
    enterMainArea: 'Enter main area',
    subArea: 'Sub Area:',
    enterSubArea: 'Enter sub area',
    address: 'Address:',
    enterAddress: 'Enter address',
    contactPerson: 'Contact Person:',
    enterContactPerson: 'Enter contact person name',
    jobTitle: 'Job Title:',
    enterJobTitle: 'Enter job title',
    phoneNumber: 'Phone Number:',
    enterPhoneNumber: 'Enter phone number',
    assignedSalesUser: 'Assigned Sales User:',
    selectSalesUser: 'Select sales user',
    unassigned: 'Unassigned',
    createButton: 'Create Customer',
    cancelButton: 'Cancel',
    errorMessage: 'Failed to create customer.',
    failedLoadUsers: 'Failed to load sales users.',
  },
  ar: {
    addCustomer: 'إضافة عميل جديد',
    createCustomer: 'إنشاء عميل',
    formIntro: 'سجل معلومات المؤسسة والموقع وبيانات التواصل لإضافة العميل.',
    companySection: 'بيانات المؤسسة',
    assignmentSection: 'التعيين',
    locationSection: 'الموقع',
    contactSection: 'مسؤول التواصل',
    generatedCustomerId: 'سيتم إنشاء رقم العميل تلقائيًا.',
    customerName: 'الاسم الكامل:',
    enterCustomerName: 'أدخل الاسم الكامل',
    organizationName: 'اسم المؤسسة:',
    enterOrganizationName: 'أدخل اسم المؤسسة',
    businessActivity: 'النشاط التجاري:',
    enterBusinessActivity: 'أدخل النشاط التجاري',
    governorate: 'المحافظة:',
    enterGovernorate: 'أدخل المحافظة',
    mainArea: 'المنطقة الرئيسية:',
    enterMainArea: 'أدخل المنطقة الرئيسية',
    subArea: 'المنطقة الفرعية:',
    enterSubArea: 'أدخل المنطقة الفرعية',
    address: 'العنوان:',
    enterAddress: 'أدخل العنوان',
    contactPerson: 'الشخص المسؤول:',
    enterContactPerson: 'أدخل اسم الشخص المسؤول',
    jobTitle: 'المسمى الوظيفي:',
    enterJobTitle: 'أدخل المسمى الوظيفي',
    phoneNumber: 'رقم الهاتف:',
    enterPhoneNumber: 'أدخل رقم الهاتف',
    assignedSalesUser: 'مندوب المبيعات المسؤول:',
    selectSalesUser: 'اختر مندوب المبيعات',
    unassigned: 'غير معين',
    createButton: 'إنشاء العميل',
    cancelButton: 'إلغاء',
    errorMessage: 'فشل في إنشاء العميل.',
    failedLoadUsers: 'فشل تحميل مستخدمي المبيعات.',
  },
};

const AddCustomer = () => {
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState({
    fullName: '',
    organizationName: '',
    businessActivity: '',
    governorate: '',
    mainArea: '',
    subArea: '',
    address: '',
    contactPerson: '',
    jobTitle: '',
    phoneNumber: '',
  });
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedSalesUserId, setSelectedSalesUserId] = useState('');
  const [error, setError] = useState(null);
  const [language, setLanguage] = usePersistentLanguage();

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (!token) {
      return { userType: null };
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        userType: payload.userType,
        requesterUsername: payload.username,
      };
    } catch (parseError) {
      console.error('Error parsing token payload:', parseError);
      return { userType: null };
    }
  }, [token]);

  const canAssignCustomer = ['admin', 'manager', 'sales-manager'].includes(
    userInfo.userType
  );

  useEffect(() => {
    if (!canAssignCustomer) {
      return;
    }

    const fetchSalesUsers = async () => {
      try {
        const response = await getUsers({ roles: 'sales' });
        const activeSalesUsers = (response.data || []).filter(
          (user) => user.userType === 'sales' && user.isActive !== false
        );
        setSalesUsers(activeSalesUsers);
      } catch (err) {
        console.error('Error loading sales users:', err);
        setError(translations[language].failedLoadUsers);
      }
    };

    fetchSalesUsers();
  }, [canAssignCustomer, language]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCustomerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...customerData };
      if (canAssignCustomer && selectedSalesUserId) {
        payload.salesUserId = selectedSalesUserId;
      }
      await createCustomer(payload);
      navigate('/customers');
    } catch (err) {
      setError(err.response?.data?.message || translations[language].errorMessage);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  const toggleLanguage = () => {
    setLanguage((prevLang) => (prevLang === 'en' ? 'ar' : 'en'));
  };

  const t = (key) => translations[language][key];

  return (
    <AppLayout
      title={t('addCustomer')}
      subtitle={t('formIntro')}
      language={language}
      onToggleLanguage={toggleLanguage}
      userType={userInfo.userType}
      showHome
      homeLabel={t('cancelButton')}
      onHome={handleCancel}
      contentClassName={styles.page}
    >
      <section className={styles.content}>
        <form onSubmit={handleSubmit} className={styles.formCard}>
          <header className={styles.formHeader}>
            <h2>{t('createCustomer')}</h2>
            <p>{t('generatedCustomerId')}</p>
          </header>

          {error && <div className={styles.errorBanner}>{error}</div>}

          <section className={styles.formSection}>
            <h3 className={styles.sectionTitle}>{t('companySection')}</h3>
            <div className={styles.formGrid}>
              <label className={styles.inputField}>
                <span>{t('customerName')}</span>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={customerData.fullName}
                  onChange={handleChange}
                  required
                  placeholder={t('enterCustomerName')}
                  className={styles.input}
                />
              </label>
              <label className={styles.inputField}>
                <span>{t('organizationName')}</span>
                <input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={customerData.organizationName}
                  onChange={handleChange}
                  required
                  placeholder={t('enterOrganizationName')}
                  className={styles.input}
                />
              </label>
              <label className={styles.inputField}>
                <span>{t('businessActivity')}</span>
                <input
                  type="text"
                  id="businessActivity"
                  name="businessActivity"
                  value={customerData.businessActivity}
                  onChange={handleChange}
                  required
                  placeholder={t('enterBusinessActivity')}
                  className={styles.input}
                />
              </label>
            </div>
          </section>

          {canAssignCustomer && (
            <section className={styles.formSection}>
              <h3 className={styles.sectionTitle}>{t('assignmentSection')}</h3>
              <div className={styles.formGrid}>
                <label className={styles.inputField}>
                  <span>{t('assignedSalesUser')}</span>
                  <select
                    value={selectedSalesUserId}
                    onChange={(event) => setSelectedSalesUserId(event.target.value)}
                    className={styles.input}
                  >
                    <option value="">{t('unassigned')}</option>
                    {salesUsers.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>
          )}

          <section className={styles.formSection}>
            <h3 className={styles.sectionTitle}>{t('locationSection')}</h3>
            <div className={styles.formGrid}>
              <label className={styles.inputField}>
                <span>{t('governorate')}</span>
                <input
                  type="text"
                  id="governorate"
                  name="governorate"
                  value={customerData.governorate}
                  onChange={handleChange}
                  required
                  placeholder={t('enterGovernorate')}
                  className={styles.input}
                />
              </label>
              <label className={styles.inputField}>
                <span>{t('mainArea')}</span>
                <input
                  type="text"
                  id="mainArea"
                  name="mainArea"
                  value={customerData.mainArea}
                  onChange={handleChange}
                  required
                  placeholder={t('enterMainArea')}
                  className={styles.input}
                />
              </label>
              <label className={styles.inputField}>
                <span>{t('subArea')}</span>
                <input
                  type="text"
                  id="subArea"
                  name="subArea"
                  value={customerData.subArea}
                  onChange={handleChange}
                  required
                  placeholder={t('enterSubArea')}
                  className={styles.input}
                />
              </label>
              <label className={`${styles.inputField} ${styles.fullWidth}`}>
                <span>{t('address')}</span>
                <textarea
                  id="address"
                  name="address"
                  value={customerData.address}
                  onChange={handleChange}
                  required
                  placeholder={t('enterAddress')}
                  className={`${styles.input} ${styles.textarea}`}
                  rows={3}
                />
              </label>
            </div>
          </section>

          <section className={styles.formSection}>
            <h3 className={styles.sectionTitle}>{t('contactSection')}</h3>
            <div className={styles.formGrid}>
              <label className={styles.inputField}>
                <span>{t('contactPerson')}</span>
                <input
                  type="text"
                  id="contactPerson"
                  name="contactPerson"
                  value={customerData.contactPerson}
                  onChange={handleChange}
                  required
                  placeholder={t('enterContactPerson')}
                  className={styles.input}
                />
              </label>
              <label className={styles.inputField}>
                <span>{t('jobTitle')}</span>
                <input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  value={customerData.jobTitle}
                  onChange={handleChange}
                  required
                  placeholder={t('enterJobTitle')}
                  className={styles.input}
                />
              </label>
              <label className={styles.inputField}>
                <span>{t('phoneNumber')}</span>
                <input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={customerData.phoneNumber}
                  onChange={handleChange}
                  required
                  placeholder={t('enterPhoneNumber')}
                  className={styles.input}
                />
              </label>
            </div>
          </section>

          <div className={styles.formActions}>
            <button type="submit" className={styles.primaryButton}>
              {t('createButton')}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={handleCancel}>
              {t('cancelButton')}
            </button>
          </div>
        </form>
      </section>
    </AppLayout>
  );
};

export default AddCustomer;
